/**
 * A generic component for a single Folder. This knows nothing about Treebeard, and can be used
 * outside of the <FolderTree> component
 */

import React, { DragEvent, ElementType, ReactElement } from "react";
import classNames from "classnames";
import { observer } from "mobx-react";
import { observable, action, computed } from "mobx";
import { handleMouseUpByBlurring } from "../../../../utils/focus";

const BASE_CLASS = "folder-component";

interface IFolderComponentProps {
  name: string;
  icon: ReactElement;
  selected: boolean;

  onClick?: () => void;
  onDrop?: (event: React.DragEvent) => void;

  // An optional element to put in place of the 'name'
  overrideNameComponent?: ReactElement;
  as: ElementType;

  disableSelect: boolean;

  /**
   * Note that we don't simply infer `disableDrop` from the non-existence of the `onDrop` callback.
   * `disableDrop` is for the case where the component is normally drop-able, but is temporarily
   * disabled to indicate that it is currently an invalid drop target - and this state comes with
   * it's own styling.
   */
  disableDrop: boolean;
}

@observer
export class FolderComponent extends React.Component<IFolderComponentProps> {
  public static defaultProps = {
    as: "button",
    disableSelect: false,
    disableDrop: false,
  };

  // Instead of just a simple `isDragHovering` boolean, we need to keep count of the number of times
  // `onDragEnter` and `onDragLeave` has been called. This is due to the way these events trigger
  // on child elements:
  // https://stackoverflow.com/questions/7110353/html5-dragleave-fired-when-hovering-a-child-element
  @observable.ref
  private dragHoverCounter = 0;

  constructor(props: IFolderComponentProps) {
    super(props);
  }

  private handleClick(): void {
    const { onClick, disableSelect } = this.props;
    if (!disableSelect && !!onClick) {
      onClick();
    }
  }

  public render(): React.ReactElement {
    const { name, icon, selected, overrideNameComponent, as: Element, disableSelect, disableDrop } = this.props;
    const handleClick = this.handleClick.bind(this);

    return (
      <Element
        className={classNames(BASE_CLASS, {
          [`${BASE_CLASS}--drag-hover`]: !disableDrop && this.isDragHovering,
          [`${BASE_CLASS}--selected`]: selected,
          [`${BASE_CLASS}--disable-select`]: disableSelect,
          [`${BASE_CLASS}--disable-drop`]: disableDrop,
        })}
        onClick={handleClick}
        onDrop={this.dropHandler}
        onMouseUp={handleMouseUpByBlurring}
        onDragEnter={this.onDragEnter}
        onDragLeave={this.onDragLeave}
        onDragOver={(event: DragEvent): void => {
          event.preventDefault();
        }}
      >
        <div className={`${BASE_CLASS}__icon`}>{icon}</div>
        {!!overrideNameComponent ? overrideNameComponent : <div className={`${BASE_CLASS}__name`}>{name}</div>}
      </Element>
    );
  }

  @computed
  private get isDragHovering(): boolean {
    return this.dragHoverCounter > 0;
  }

  @action.bound
  private onDragEnter(event: React.DragEvent): void {
    event.preventDefault();
    ++this.dragHoverCounter;
  }

  @action.bound
  private onDragLeave(event: React.DragEvent): void {
    event.preventDefault();
    --this.dragHoverCounter;
  }

  @action.bound
  private dropHandler(event: React.DragEvent): void {
    event.preventDefault();

    this.dragHoverCounter = 0;

    const { onDrop, disableDrop } = this.props;
    if (!!onDrop && !disableDrop) {
      onDrop(event);
    }
  }
}
